// ============================================================================
// ETAPAS — la hoja donde el DUEÑO administra las columnas de su embudo (T-EMB-1,
// R-EMB-01). Crear · renombrar · color · marcar «final» · reordenar · borrar.
//
// Por qué existe: la RLS del dueño sobre `etapas` está desde la 0005, pero no
// había una sola pantalla que la usara — el vacío del embudo decía «configura
// las etapas del negocio» sin decir dónde. Norma 12 de la casa: lo que el
// cliente parametriza se entrega editable, no se pregunta.
//
// ⚠️ Cada acción escribe y DESPUÉS vuelve a leer las etapas de la base
//    (`listarEtapas`): el orden y los ids los decide la base, no esta pantalla.
//    Sin esa relectura, un `reordenar_etapas` rechazado dejaría la lista de la
//    pantalla en un orden que la base nunca guardó.
// ⚠️ Borrar una etapa CON contactos los dejaría «Sin etapa» sin aviso (la FK es
//    `on delete set null`, 0005:29-31). Por eso el botón se deshabilita y dice
//    cuántos hay que mover primero. Es la guarda de la pantalla; la base no la
//    tiene y este tramo no la añade (queda dicho).
// ============================================================================
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";
import { Aviso, Boton, Interruptor } from "./piezas.tsx";
import {
  actualizarEtapa, borrarEtapa, crearEtapa, explicarError, listarEtapas, reordenarEtapas,
} from "./datos.ts";
import type { Etapa } from "./tipos.ts";

/** Paleta fija: ocho tonos que se distinguen entre sí en claro y en oscuro. */
export const PALETA_ETAPAS = [
  "#6e6e73", "#0a7ea4", "#a16207", "#248a3d", "#7c3aed", "#c11315", "#c07600", "#be185d",
] as const;

/** Las ids en el orden nuevo tras mover la posición `i` una casilla (`-1` arriba, `+1` abajo). */
export function ordenTrasMover(etapas: Etapa[], i: number, delta: -1 | 1): string[] | null {
  const j = i + delta;
  if (j < 0 || j >= etapas.length) return null;
  const ids = etapas.map((e) => e.id);
  [ids[i], ids[j]] = [ids[j], ids[i]];
  return ids;
}

export interface EtapasEditorProps {
  sb: any;
  tenantId: string;
  etapas: Etapa[];
  /** Contactos por etapa (id → cuántos). Decide si se puede borrar. */
  conteo: Record<string, number>;
  /** Se llama con la lista RELEÍDA de la base después de cada cambio. */
  onCambio: (etapas: Etapa[]) => void;
  onCerrar: () => void;
}

export function EtapasEditor({ sb, tenantId, etapas, conteo, onCambio, onCerrar }: EtapasEditorProps) {
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [nueva, setNueva] = useState("");
  const [editando, setEditando] = useState<{ id: string; nombre: string } | null>(null);
  const [paletaDe, setPaletaDe] = useState<string | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCerrar]);

  /** Escribe, relee, avisa. Un error se muestra y la lista NO se toca. */
  async function con(accion: () => Promise<void>) {
    if (ocupado) return;
    setOcupado(true); setError(null);
    try {
      await accion();
      onCambio(await listarEtapas(sb, tenantId));
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setOcupado(false);
    }
  }

  function agregar() {
    const nombre = nueva.trim();
    if (!nombre) return;
    void con(async () => {
      // El orden (máximo + 1) lo calcula `crearEtapa` (T-BAS-0): aquí solo el nombre limpio.
      await crearEtapa(sb, tenantId, { nombre, color: PALETA_ETAPAS[0], es_final: false });
      setNueva("");
    });
  }

  function renombrar() {
    if (!editando) return;
    const nombre = editando.nombre.trim();
    const actual = etapas.find((e) => e.id === editando.id);
    setEditando(null);
    if (!nombre || !actual || nombre === actual.nombre) return;
    void con(() => actualizarEtapa(sb, editando.id, { nombre }));
  }

  function mover(i: number, delta: -1 | 1) {
    const ids = ordenTrasMover(etapas, i, delta);
    if (!ids) return;
    void con(() => reordenarEtapas(sb, ids));
  }

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Etapas del embudo">
      <button className="flex-1 bg-black/40 cursor-pointer" aria-label="Cerrar etapas"
        onClick={onCerrar} />
      <aside className="entra w-[92%] max-w-[400px] h-full flex flex-col bg-superficie border-l border-borde-suave
        shadow-[var(--shadow-flotante)]">
        <header className="shrink-0 flex items-center gap-2 px-4 pt-4 pb-3 border-b border-borde-suave">
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-semibold tracking-tight">Etapas del embudo</h2>
            <p className="text-[12.5px] text-tinta-2 mt-0.5">
              Las <span className="font-medium">finales</span> se pliegan en «Cerradas» para dejar el tablero limpio.
            </p>
          </div>
          <button onClick={onCerrar} aria-label="Cerrar" className="p-2 rounded-md text-tinta-2 hover:text-tinta
            active:scale-90 transition-transform cursor-pointer"><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-y-auto scroll-fino px-3 py-3 space-y-2">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {etapas.length === 0 && (
            <p className="text-[13px] text-tinta-2 text-center py-6">Todavía no hay etapas. Crea la primera abajo.</p>
          )}
          <ul className="space-y-1.5">
            {etapas.map((e, i) => {
              const n = conteo[e.id] ?? 0;
              const enEdicion = editando?.id === e.id;
              return (
                <li key={e.id} data-etapa-fila={e.id}
                  className="rounded-xl border border-borde-suave bg-elevado/60 px-2.5 py-2">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col -my-1">
                      <button onClick={() => mover(i, -1)} disabled={ocupado || i === 0}
                        aria-label={`Subir ${e.nombre}`}
                        className="p-0.5 rounded text-tinta-2 hover:text-tinta disabled:opacity-25 cursor-pointer disabled:cursor-default">
                        <ArrowUp size={14} /></button>
                      <button onClick={() => mover(i, 1)} disabled={ocupado || i === etapas.length - 1}
                        aria-label={`Bajar ${e.nombre}`}
                        className="p-0.5 rounded text-tinta-2 hover:text-tinta disabled:opacity-25 cursor-pointer disabled:cursor-default">
                        <ArrowDown size={14} /></button>
                    </div>
                    <button onClick={() => setPaletaDe(paletaDe === e.id ? null : e.id)}
                      aria-label={`Color de ${e.nombre}`} aria-expanded={paletaDe === e.id}
                      className="h-5 w-5 rounded-full border-2 border-white shadow-sm shrink-0 cursor-pointer
                        active:scale-90 transition-transform"
                      style={{ background: e.color }} />
                    {enEdicion ? (
                      <input autoFocus value={editando!.nombre} aria-label={`Nombre de ${e.nombre}`}
                        onChange={(ev) => setEditando({ id: e.id, nombre: ev.target.value })}
                        onBlur={renombrar}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter") renombrar();
                          if (ev.key === "Escape") { ev.stopPropagation(); setEditando(null); }
                        }}
                        className="min-w-0 flex-1 h-8 px-2 rounded-lg bg-superficie border border-marca/50 text-[13.5px]
                          focus:outline-none" />
                    ) : (
                      <button onClick={() => setEditando({ id: e.id, nombre: e.nombre })}
                        title="Cambiar el nombre"
                        className="min-w-0 flex-1 text-left text-[13.5px] font-medium truncate h-8 px-2 rounded-lg
                          hover:bg-tinta/[0.05] cursor-pointer">
                        {e.nombre}
                      </button>
                    )}
                    <span className="text-[11.5px] text-tinta-2 cifra shrink-0" title="Contactos en esta etapa">{n}</span>
                    <button
                      onClick={() => void con(() => borrarEtapa(sb, e.id))}
                      disabled={ocupado || n > 0}
                      aria-label={`Borrar ${e.nombre}`}
                      title={n > 0 ? `Mueve primero sus ${n} contacto${n === 1 ? "" : "s"}` : "Borrar etapa"}
                      className="p-1.5 rounded-md text-tinta-2 hover:text-peligro disabled:opacity-25 cursor-pointer
                        disabled:cursor-not-allowed">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1.5 pl-7">
                    <Interruptor activo={e.es_final} deshabilitado={ocupado} etiqueta="Etapa final"
                      detalle="Se pliega en «Cerradas»" testId={`final-${e.id}`}
                      onCambiar={(v) => void con(() => actualizarEtapa(sb, e.id, { es_final: v }))} />
                  </div>
                  {paletaDe === e.id && (
                    <div className="flex flex-wrap gap-2 mt-2 pl-7" role="group" aria-label={`Colores para ${e.nombre}`}>
                      {PALETA_ETAPAS.map((c) => (
                        <button key={c} aria-label={`Color ${c}`} aria-pressed={e.color === c}
                          onClick={() => { setPaletaDe(null); void con(() => actualizarEtapa(sb, e.id, { color: c })); }}
                          className={`h-6 w-6 rounded-full cursor-pointer transition-transform active:scale-90
                            ${e.color === c ? "ring-2 ring-offset-2 ring-tinta" : ""}`}
                          style={{ background: c }} />
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <form className="shrink-0 flex items-center gap-2 px-3 py-3 border-t border-borde-suave"
          onSubmit={(ev) => { ev.preventDefault(); agregar(); }}>
          <input value={nueva} onChange={(ev) => setNueva(ev.target.value)} placeholder="Nueva etapa, p. ej. Desertado"
            aria-label="Nombre de la etapa nueva" maxLength={40}
            className="min-w-0 flex-1 h-10 md:h-9 px-3 rounded-lg bg-elevado border border-borde text-sm
              placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50" />
          <Boton type="submit" tono="marca" tam="sm" disabled={ocupado || !nueva.trim()}>
            <Plus size={14} /> Agregar
          </Boton>
        </form>
      </aside>
    </div>
  );
}
